<script setup lang="ts">
import {
  computed,
  ref,
} from 'vue';

import type { IAffiliateTrackerPayload } from '@modules/affiliate-trackers/types';
import type {
  FormSchema,
  VFormRef,
} from '@modules/forms/components/VForm/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VDialog from '@components/dialogs/VDialog/VDialog.vue';
import { TRANSLATION_GROUP_AFFILIATE_TRACKERS } from '@modules/affiliate-trackers/constants';
import { useAffiliateTrackersCreateEditMutation } from '@modules/affiliate-trackers/mutations';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';

import type { AffiliateTrackersAddButtonProps } from './types';

withDefaults(defineProps<AffiliateTrackersAddButtonProps>(), {});

const { getTranslation } = useI18n();

const {
  mutateAsync,
  isLoading,
  errors,
  clearError,
} = useAffiliateTrackersCreateEditMutation();

const formSchema = computed<FormSchema<IAffiliateTrackerPayload>>(() => ({
  description: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_TRACKERS, 'MODAL_CREATE_DESCRIPTION').value,
    required: true,
  },
}));

const form = ref<VFormRef>();
const isVisibleForm = ref(false);

function showModal(): void {
  isVisibleForm.value = true;
}

function onModalHide(): void {
  isVisibleForm.value = false;
}

function onSubmit(): void {
  form.value?.submit();
}

async function onFormSave(values: IAffiliateTrackerPayload): Promise<void> {
  await mutateAsync(values);
  onModalHide();
}
</script>

<template>
  <div v-data-qa-auto-id="'AffiliateTrackersAddButton'">
    <VButton
      :disabled="disabled"
      :label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_TRACKERS, 'BUTTON_ADD')"
      :loading="isLoading"
      @click="showModal"
    />
    <VDialog
      v-model:visible="isVisibleForm"
      :title="$getTranslation(TRANSLATION_GROUP_AFFILIATE_TRACKERS, 'MODAL_CREATE_TITLE')"
    >
      <VForm
        ref="form"
        :schema="formSchema"
        :is-loading="isLoading"
        :custom-errors="errors"
        @interacted="clearError"
        @submit="onFormSave"
      />
      <template #footer>
        <VButton
          :label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_TRACKERS, 'MODAL_CREATE_CANCEL')"
          outlined
          @click="onModalHide"
        />
        <VButton
          :label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_TRACKERS, 'MODAL_CREATE_OK')"
          @click="onSubmit"
        />
      </template>
    </VDialog>
  </div>
</template>
